<template>
	<div v-if="promoDetails" class="full-width column flexible article separated-extra">
        <header class="row centering separated">
            <h1 class="rigid capitalize">Review</h1>
        </header>
        <ul v-if="promoDetails.items[0]" class="column panel">
            <li class="row centering separated-extra">
                <div class="rigid circle">
                    <i class="far fa-folder jumbo"></i>
                </div>
                <div class="column separated flexible">
                    <h2 class="flexible">{{promoDetails.items[0].name}}</h2>
                    <div class="row centering separated">
                        <div v-if="promoDetails.items[0].cost" class="rigid gray-6">{{promoDetails.items[0].cost}}</div>
                        <div class="rigid badge badge-success badge-pill">{{promoDetails.items[0].offer}}</div>
                    </div>
                </div>
            </li>
            <li v-if="promoDetails.items[1]" class="row centering separated-extra">
                <div class="rigid circle">
                    <i class="fas fa-laptop jumbo"></i>
                </div>
                <div class="column separated flexible">
                    <h2 class="flexible">{{promoDetails.items[1].name}}</h2>
                    <div class="row centering separated">
                        <div v-if="promoDetails.items[1].cost" class="rigid gray-6">{{promoDetails.items[1].cost}}</div>
                        <div class="rigid badge badge-success badge-pill">{{promoDetails.items[1].offer}}</div>
                    </div>
                </div>
            </li>
        </ul>
		<span class="column separated-extra" v-if="promoDetails.collectPayment">
			<payment-notice
				v-if="account.paymentMethod"
				:on-click="back"
				:model="account.paymentMethod">
			</payment-notice>

			<div v-if="hasUsAddress" class="notice info row separated rigid">
				<i class="fas fa-info-circle rigid"></i>
				<div class="flexible">
				Your card will not be charged until {{renewalDate | date}}. You may opt-out at any time.
				</div>
			</div>
			<div v-else class="notice info row separated rigid">
				<i class="fas fa-info-circle rigid"></i>
				<div class="flexible">
					Your plan will not auto-renew. To continue your subscription <a href="mailto:sales@support.tekcloud.com?subject=Continue%20TekCloud%Subscription"><strong>contact support</strong></a> or call 1-800-833-9200.
				</div>
			</div>
		</span>
		<div v-if="tekdriveDowngradeWarning" class="notice warning">
			Your current TekDrive plan has more storage than the promotion offers. Subscribing will reduce your storage limit.
		</div>
        <div v-if="addPromotionError">
            <span v-if="addPromotionError.type === 'INVALID'" class="notice error">
                Your account is not eligable for this promotion. If this is a mistake, please <a href="mailto:sales@support.tekcloud.com?subject=Invalid%20Promotion"><strong>contact support</strong></a>.
            </span>
            <span v-else-if="addPromotionError.type === 'PROMO_NOT_AVAILABLE'" class="notice error">
                Your account is not eligable for this promotion. If this is a mistake, please <a href="mailto:sales@support.tekcloud.com?subject=Invalid%20Promotion"><strong>contact support</strong></a>.
            </span>
			<span v-else-if="addPromotionError.type === 'PROMO_NOT_FOUND'" class="notice error">
                Your account has already claimed this promotion. If this is a mistake, please <a href="mailto:sales@support.tekcloud.com?subject=Promotion%20Not%20Found"><strong>contact support</strong></a>.
            </span>
            <div v-else-if="addPromotionError.type === 'NEEDS_REVIEW'" class="notice warning">
                <p>
                    In compliance with U.S. Government regulation, Tektronix is required to review all software download activity. Our team has been notified and will contact you once your account has been cleared.
                    This process may take up to two U.S. business days. We apologize for any inconvenience.
                </p>
                <p>
                    If you have any questions, please <a href="mailto:export-control@support.tekcloud.com?subject=Account%20Under%20Review">contact support</a>.
                </p>
            </div>
            <span v-else class="notice error">
                There was an error redeeming this promotion. Please <a href="mailto:sales@support.tekcloud.com?subject=Error%20Redeeming%20Promotion"><strong>contact support</strong></a>.
            </span>
        </div>
        <footer class="row separated right centering">
            <button v-if="navigation.previous.canNavigateTo && navigation.previous.showProgress" type="button" class="link rigid" @click="back()"><small><i class="fas fa-arrow-left"></i></small> {{navigation.previous.name}}</button>
		    <a v-else-if="navigation.start.view" class="link rigid" :href="navigation.start.view"><small><i class="fas fa-arrow-left"></i></small> {{navigation.start.name}}</a>
			<span class="gap"></span>
            <router-link v-if="accountUnderReview" to="/" class="button">Account Home</router-link>
            <submit-button class="primary rigid" type="button" :on-click="subscribeToPromo" :button-text="'Subscribe'" :is-submitting="submitting" :is-disabled="submitting"></submit-button>
		</footer>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import SubmitButton from '@/components/SubmitButton';
import PaymentNotice from '@/components/PaymentNotice';
import Spinner from '@/components/Spinner';
import date from '@/filters/date';

export default {
	name: 'Review',
	props: {
		navigation: Object,
		promoCode: String,
		promoDetails: Object
	},
	filters: {
		date
	},
	data(){
		return {
			submitting: false,
			addPromotionError: null,
		};
	},
	components:{
		SubmitButton,
		Spinner,
		PaymentNotice
	},
	computed:{
		...mapGetters([
			'address',
			'hasUsAddress',
			'hasNonUsAddress',
			'userStatus',
			'account'
		]),
		...mapGetters({
			highestDrivePlan: 'drive/highestPlan'
		}),
		renewalDate() {
			const now = new Date();
			return new Date(now.setMonth(now.getMonth()+6));
		},
		accountUnderReview() {
			return this.addPromotionError && this.addPromotionError.type === 'NEEDS_REVIEW';
		},
		tekdriveDowngradeWarning() {
			return this.highestDrivePlan &&
				this.compareStorageValues(this.highestDrivePlan.details.storageSizeLimit.value, this.promoDetails.items[0].details.storageSizeLimit.value);
		}
	},
	methods:{
		subscribeToPromo() {
			const vm = this;
			this.submitting = true;
			this.$store.dispatch('subscription/ADD_PROMOTION_REQUEST', this.promoCode)
				.then(response => {
					vm.submitting = false;
					vm.next();
				})
				.catch(reason => {
					vm.submitting = false;
					vm.addPromotionError = reason;
					// set export is pending flag
					if (vm.addPromotionError.type === 'NEEDS_REVIEW') {
						vm.$store.dispatch('SET_EXPORT_FLAG', true);
					}
				});
		},
		goToPayment() {
			this.$emit('navigation', {
				current: 'PaymentMethod',
				currentLabel: 'Setup',
				next: 'Review',
				previous: this.navigation.previous,
				previousLabel: 'Promotion',
				isLink: false
			});
		},
		compareStorageValues(currentStorage, newStorage){
			return (currentStorage > newStorage);
		},
		next() {
			this.$emit('navigation', this.navigation.currentIndex + 1);
		},
		back() {
			this.$emit('navigation', this.navigation.currentIndex - 1);
		},
	},
	created() {
		this.$store.dispatch('subscription/GET_TEK_SUBSCRIPTIONS_REQUEST');
	}
};
</script>

<style scoped>
section {
    margin-bottom: 0;
}

.panel + .payment-notice {
    margin-bottom: 0;
}

.article > footer {
    margin-top: 2em;
}

.panel h2 {
    margin-bottom: 1rem;
    margin-top: 0;
}
.panel {
	background: white;
	border-radius: 6px;
	padding: 2rem;
	border: 2px solid #dfdfdf;
	width: 100%;
	margin: auto;
	box-sizing: border-box;
}
.dark .panel {
	background: #1a1a1a;
	border: 2px solid #333;
}
ul.panel {
    padding: 0;
}

ul.panel li {
    padding: 2rem;
}

.panel li + li {
    border-top: 1px solid #dfdfdf;
}
.dark .panel li + li {
    border-top: 1px solid #333;
}
.row.separated-extra > * + .flexible {
    margin-left: 1.25em;
}

.panel + footer {
	margin-top: 2em;
}

.circle {
    background: #EBF6FF;
    height: 4rem;
    width: 4rem;
    border-radius: 50%;
    overflow: hidden;
    position: relative;
}

.dark .circle {
    background: #243044;
}

.circle > i {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    color: #7CB9EE;
    bottom: 0;
    font-size: 3rem;
    text-align: center;
}

.circle > i.fa-laptop {
    font-size: 2.5rem;
    top: 20px;
}
.circle > i.fa-folder {
    top: 18px;
}

</style>



// WEBPACK FOOTER //
// ./src/views/promotions/Review.vue